/* eslint-disable no-restricted-globals,class-methods-use-this,react/jsx-filename-extension */
import React from 'react';
import { flushSync } from 'react-dom';
import T from 'i18n-react';
import get from 'lodash/get';
import Link from 'next/link';
import Router from 'next/router';
import qs from 'qs';

import {
	triggerFormStepCompleteEvent,
	triggerFormSubmissionError,
	triggerFormSubmittedEvent,
	triggerFormValidationErrorEvent,
} from 'Services/analytics/events';
import { handleAxiosError } from 'Utilities/error/handleAxiosError';

import { validateEmail, validatePhone } from '../../services/api/miscellaneous';
import { signUp } from '../../services/api/user';
import FormBase from '../../utilities/FormBase/FormBase';
import {
	isEmpty,
	isFunction,
	isNull,
	isString,
	isValidEmail,
	normaliseVatValue,
	postcodeValidate,
	scrollIntoView,
	vatValidate,
	websiteValidate,
} from '../../utilities/helpers';

import { FORM_STEPS, STATUS } from './SignupPageConsts';

T.setTexts(require('../texts.json'));

/**
 * This component contains Signup page logic layer
 */

class SigupPageBackend extends FormBase {
	getRangeFieldKey = (name) => name.replace(/From|To/, '');

	goToNextStep = (next) => this.setState({ nextFormStep: next });

	customInputFormat = (id, value) => {
		const { formStep } = this.state;

		if (!this.fields[formStep].includes(id)) {
			return value;
		}

		const rangeFieldLimit = this.getRangeFieldKey(id) === 'age' ? 2 : 6;

		if ([...this.rangeFromFields, ...this.rangeToFields].includes(id)) {
			const v = isNull(value) ? '' : value;
			const r = parseInt(v.toString().substr(0, rangeFieldLimit), 10);
			return isNaN(r) ? '' : r;
		}

		switch (id) {
			case 'makes':
				return value.sort((a, b) => a - b);
			case 'allMakes':
				return !!parseInt(value, 10);
			case 'contactPhone':
				return value.replace(/\D+/g, '');
			case 'companyPhone':
				return value.replace(/\D+/g, '');
			case 'email':
				return value.toLowerCase();
			case 'postcode':
				return value.toUpperCase();
			default:
				return value;
		}
	};

	customErrorMessage = (fieldId) => {
		const { status } = this.state;
		const value = this.getValue(fieldId);

		if (this.rangeToFields.includes(fieldId)) {
			const field = this.getRangeFieldKey(fieldId);
			const toValue = this.getValue(`${field}To`);
			const fromValue = this.getValue(`${field}From`);

			if (!isEmpty(toValue) && !isEmpty(fromValue) && fromValue >= toValue) {
				return `${fieldId}:toSmall`;
			}
		}

		switch (fieldId) {
			case 'postcode':
				return `postcode${value ? ':invalid' : ''}`;
			case 'website':
				return `website${value ? ':invalid' : ''}`;
			case 'vatNumber':
				return `vatNumber${value ? ':invalid' : ''}`;
			case 'contactPhone':
				return `contactPhone${value ? ':invalid' : ''}`;
			case 'companyPhone':
				return `companyPhone${value ? ':invalid' : ''}`;
			case 'email': {
				let ending = '';
				if (!this.checkValidity(fieldId) && value) {
					ending = ':invalid';
				}

				if (status === STATUS.EMAIL_EXISTS) {
					return (
						<>
							{T.translate(`${this.textsPrefix}.errorMessages.email:used`, {
								contactUs: (
									<Link as="/contact-us" href="/static_page">
										{T.translate(`${this.textsPrefix}.errorMessages.contactUs`)}
									</Link>
								),
								signIn: <Link href="/signin">{T.translate(`${this.textsPrefix}.errorMessages.signIn`)}</Link>,
							})}
						</>
					);
				}

				return `email${ending}`;
			}
			default:
				return fieldId;
		}
	};

	customCheckValidity = (id, valid, el) => {
		const isDom = isFunction(el.checkValidity);
		const value = this.getValue(id);
		const isCurrenlyValid = this.getInputProp(id, 'valid');

		if (this.rangeToFields.includes(id)) {
			const field = this.getRangeFieldKey(id);
			const toValue = this.getValue(`${field}To`);
			const fromValue = this.getValue(`${field}From`);

			return !isEmpty(toValue) && toValue > fromValue;
		}

		switch (id) {
			case 'postcode':
				return postcodeValidate(value);
			case 'website':
				return websiteValidate(value);
			case 'vatNumber':
				return vatValidate(value);
			case 'email':
			case 'contactPhone':
			case 'companyPhone': {
				if (isDom) {
					return isCurrenlyValid;
				}
				return valid;
			}
			default:
				return valid;
		}
	};

	customResetFormStateResolver = (prevState) => ({
		makesList: prevState.makesList,
	});

	customGetTextInputResolver = ({ name }) => {
		// from-to fields should display error tooltip below the input
		const field = this.getRangeFieldKey(name);
		const feedbackFillWidth = this.rangeFields.includes(field);
		const feedbackPlacement = this.rangeFields.includes(field) ? 'bottom' : 'right';

		return {
			feedbackTooltip: {
				content: this.getErrorMessage(name),
				fillWidth: feedbackFillWidth,
				placement: feedbackPlacement,
			},
		};
	};

	customValidation = (updateNextInputs) => {
		const { purchaseAllMakers } = this.state;

		if (!this.getValue('buysAllMakes') && !this.getValue('makes').length) {
			updateNextInputs('makes', { valid: false });
		}

		if (purchaseAllMakers === null) {
			updateNextInputs('buysAllMakes', { valid: false });
			updateNextInputs('makes', { valid: false });
		}

		if (this.getValue('howDidYouHearAboutUs') !== 'Other') {
			updateNextInputs('howDidYouHearAboutUsOtherDescription', { valid: true });
		}
	};

	customNearestInvalid = (nearestInvalid) => {
		if (nearestInvalid === 'priceFrom' && !this.getValue('makes').length) {
			return 'makes-section';
		}

		return nearestInvalid;
	};

	customOnBlur = async (id, isValid) => {
		const value = this.getValue(id);

		if (this.rangeFromFields.includes(id)) {
			const field = this.getRangeFieldKey(name);
			this.updateInputState(`${field}From`, { valid: this.checkValidity(`${field}To`) });
		}

		switch (id) {
			case 'contactPhone':
			case 'companyPhone': {
				if (!isEmpty(value)) {
					this.setState({ validatingPhone: true });
					const { valid } = await validatePhone(value);
					this.setState({ validatingPhone: false });
					return valid;
				}
				return false;
			}
			case 'email': {
				if (!isValidEmail(value)) {
					return false;
				}
				if (!isEmpty(value)) {
					this.setState({ validatingEmail: true });
					const { status, valid } = await validateEmail(value);
					this.setState({ status: status || undefined, validatingEmail: false });
					return valid;
				}
				break;
			}
			default:
				break;
		}

		return isValid;
	};

	validationInterceptor = (nextInputs) => {
		const { formStep } = this.state;
		const currentInputsSet = this.fields[formStep];
		const currentInputs = nextInputs.filter((input) => currentInputsSet.includes(input.name));
		const currentInvalid = currentInputs.filter((i) => !i.valid);
		currentInvalid.forEach((input) => {
			if (input.showIfInvalid) {
				triggerFormValidationErrorEvent(input.name);
			}
		});
		return !currentInvalid.length;
	};

	onMultiselectChange = ({ value }) => {
		this.updateInputState('makes', {
			valid: !!value.length,
			value: value.map((v) => v.value),
		});
	};

	onPurchaseAllMakersChange = (e) => {
		const buyAllMakes = this.getInputFormat('allMakes', e.target.value);

		this.setState({
			purchaseAllMakers: buyAllMakes,
		});

		this.updateInputState('buysAllMakes', { valid: true, value: buyAllMakes });

		if (buyAllMakes) {
			this.updateInputState('makes', { valid: true, value: [] });
		}
	};

	onPopStateChange = (e) => {
		const {
			state: { url },
		} = e;
		const {
			formSteps,
			index,
			progressStepsIndicator: { steps },
			status,
		} = this.state;
		const isSignUp = url.includes('/signup');
		const queryParams = qs.parse(url.split('?')[1]);
		const newIndex = parseInt(queryParams.step) - 1 || 0; // Remove 1 to align displayed step in qs with index.

		// Not part of Signup
		if (!isSignUp) {
			return null;
		}

		// If the form is complete, reset the form.
		if (status === STATUS.SEND) {
			const newSteps = steps.map((entry) => ({ ...entry, completed: false }));
			const { historyLog } = this.state;
			const startOfForm = -window.history.length + historyLog + 1;

			// Reset History
			window.history.go(startOfForm);

			return this.resetForm({
				progressStepsIndicator: {
					steps: newSteps,
				},
			});
		}

		// If the user is moving forward and the form is invalid
		if (newIndex > index && !this.validateForm()) {
			window.history.go(-1);
			return null;
		}
		if (newIndex > index) {
			// Moving forward, set entry tick to completed.
			steps[index].completed = true;
		} else {
			// Moving backward, previous entry is not completed.
			steps[newIndex].completed = false;
		}

		return this.setState((prevState) => ({
			...prevState,
			formStep: formSteps[newIndex],
			index: newIndex,
			progressStepsIndicator: {
				steps,
			},
		}));
	};

	onSubmitSuccess = () => {
		const { dealerID, formStep, historyLog, index, nextFormStep } = this.state;

		const nextIndex = index + 1;
		const url = `/signup?step=${nextIndex + 1}`; // Display step as one above index.

		Router.push(url, url, { shallow: true });

		triggerFormStepCompleteEvent(`${dealerID}: ${formStep} Step`);

		if (formStep === FORM_STEPS.REQUIREMENTS) {
			triggerFormSubmittedEvent(`${dealerID}: Form submitted`);

			this.resetForm({
				historyLog,
				status: STATUS.SEND,
			});
		} else {
			this.setState((prevState) => {
				const { steps } = prevState.progressStepsIndicator;

				steps[index].completed = true;

				return {
					...prevState,
					formStep: nextFormStep,
					index: nextIndex,
					nextFormStep: null,
					progressStepsIndicator: {
						steps,
					},
				};
			});
		}
	};

	submit = async () => {
		const { dealerID, formStep, inputs, updateToken } = this.state;

		const body = {
			formStep,
			id: dealerID,
		};

		let scrollToElement = 'body';

		inputs.forEach((i) => {
			const { name } = i;
			let { value } = i;

			if (isString(value)) {
				value = value.trim();
			}

			body[i.name] = this.getInputFormat(name, value);
		});

		// We do not want to format the VAT number on keystrokes
		if (isString(body.vatNumber)) {
			body.vatNumber = normaliseVatValue(body.vatNumber);
		}

		try {
			this.setState({
				sending: true,
				status: STATUS.DEFAULT,
			});

			const res = await signUp(body, updateToken);

			flushSync(() =>
				this.setState({
					dealerID: get(res.data, 'id'),
					sending: false,
					updateToken: get(res.data, 'token'),
				}),
			);

			this.onSubmitSuccess();
		} catch (err) {
			handleAxiosError({ axiosError: err });
			triggerFormSubmissionError();

			this.setState({
				sending: false,
			});

			if (get(err, 'response.status') === 422 && formStep === FORM_STEPS.DEALER) {
				scrollToElement = '[for="email"]';

				this.updateInputState('email', {
					valid: false,
				});

				this.setState({
					status: STATUS.EMAIL_EXISTS,
				});
			} else {
				this.setState({
					status: STATUS.ERROR,
				});
			}
		}

		scrollIntoView(document.querySelector(scrollToElement), { behavior: 'smooth' });
	};
}

export default SigupPageBackend;
